import { differenceInMinutes, toDate } from "date-fns";

const RECENTLY_CHANGED_THRESHOLD_MINUTES = 15;
const SEEN_EVENTS_STORAGE_KEY = "seen-event-changes";

type SeenEventsMap = Record<string, number>; // uuid -> version

/**
 * Gets the key for storing seen status of an event (uuid + version)
 */
const getEventKey = (uuid: string, version: number): string =>
  `${uuid}-v${version}`;

/**
 * Retrieves the map of seen events from localStorage
 */
const getSeenEvents = (): SeenEventsMap => {
  try {
    const stored = localStorage.getItem(SEEN_EVENTS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Marks an event as seen by storing its uuid and version in localStorage
 */
export const markEventAsSeen = (uuid: string, version: number): void => {
  try {
    const seenEvents = getSeenEvents();
    seenEvents[getEventKey(uuid, version)] = Date.now();
    localStorage.setItem(SEEN_EVENTS_STORAGE_KEY, JSON.stringify(seenEvents));
  } catch {
    // Silently fail if localStorage is not available
  }
};

/**
 * Checks if an event has been marked as seen
 */
export const isEventSeen = (uuid: string, version: number): boolean => {
  try {
    const seenEvents = getSeenEvents();
    return getEventKey(uuid, version) in seenEvents;
  } catch {
    return false;
  }
};

/**
 * Determines if an event was recently changed based on its updatedAt timestamp.
 * An event is considered recently changed if it was updated within the last 15 minutes.
 * Note: The "seen" status check should be handled by the component using isEventSeen.
 */
export const isRecentlyChanged = (event: { updatedAt?: Date }): boolean => {
  if (!event.updatedAt) return false;

  const updatedAt = toDate(event.updatedAt.toString());
  const now = new Date();
  const minutesSinceUpdate = differenceInMinutes(now, updatedAt);

  return minutesSinceUpdate <= RECENTLY_CHANGED_THRESHOLD_MINUTES;
};
